import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, TrendingUp, DollarSign, Send } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function CreatorNetworkPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    platform: 'instagram',
    followers: 0,
    commission_rate: 10
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileData, creatorsData] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/merchant/creators/list')
      ]);
      setUser(profileData.member);
      setCreators(creatorsData.data.creators || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/api/merchant/creators/invite', formData);
      toast.success('Creator invited!', {
        description: `Invitation sent to ${formData.name}`
      });
      setShowInviteForm(false);
      setFormData({ email: '', name: '', platform: 'instagram', followers: 0, commission_rate: 10 });
      loadData();
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  const handleLogout = async () => {
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
      <p className="text-[#9CB0C7]">Loading...</p>
    </div>;
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Creator Network</h1>
            <p className="text-sm text-[#9CB0C7]">Creator Network</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">← Dashboard</button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Creator Network</h2>
          <p className="text-[#9CB0C7]">Partner with influencers to promote your deals</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <Users className="w-8 h-8 text-[#7A5CFF] mb-3" />
            <div className="text-2xl font-bold text-white mb-1">{creators.length}</div>
            <div className="text-sm text-[#9CB0C7]">Total Creators</div>
          </div>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <TrendingUp className="w-8 h-8 text-[#3EF1C7] mb-3" />
            <div className="text-2xl font-bold text-white mb-1">{creators.reduce((sum, c) => sum + c.total_sales, 0)}</div>
            <div className="text-sm text-[#9CB0C7]">Total Sales</div>
          </div>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <DollarSign className="w-8 h-8 text-[#8CF78E] mb-3" />
            <div className="text-2xl font-bold text-white mb-1">${creators.reduce((sum, c) => sum + c.total_commission, 0).toFixed(2)}</div>
            <div className="text-sm text-[#9CB0C7]">Commission Paid</div>
          </div>
        </div>

        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="mb-6 px-6 py-3 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90 transition-colors font-semibold flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Invite Creator
        </button>

        {showInviteForm && (
          <div className="mb-8 rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Invite New Creator</h3>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Creator Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Platform</label>
                  <select value={formData.platform} onChange={(e) => setFormData({...formData, platform: e.target.value})} className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60">
                    <option value="instagram">Instagram</option>
                    <option value="youtube">YouTube</option>
                    <option value="tiktok">TikTok</option>
                    <option value="twitter">Twitter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Followers</label>
                  <input type="number" value={formData.followers} onChange={(e) => setFormData({...formData, followers: Number(e.target.value)})} required className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Commission Rate (%)</label>
                <input type="number" step="0.1" value={formData.commission_rate} onChange={(e) => setFormData({...formData, commission_rate: Number(e.target.value)})} required className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowInviteForm(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 transition-colors font-semibold flex items-center justify-center gap-2"><Send className="w-4 h-4" />Send Invitation</button>
              </div>
            </form>
          </div>
        )}

        {creators.length > 0 && (
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#111A2E]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Creator</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Followers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Sales</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Earned</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22304A]">
                {creators.map((creator) => (
                  <tr key={creator._id} className="hover:bg-[#111A2E]/40 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white">{creator.name}</div>
                      <div className="text-xs text-[#9CB0C7]">{creator.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white capitalize">{creator.platform}</td>
                    <td className="px-6 py-4 text-sm text-white tabular-nums">{creator.followers.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-white">{creator.commission_rate}%</td>
                    <td className="px-6 py-4 text-sm text-white tabular-nums">{creator.total_sales}</td>
                    <td className="px-6 py-4 text-sm text-[#8CF78E] font-semibold">${creator.total_commission.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${
                        creator.status === 'active' ? 'bg-[#8CF78E]/10 text-[#8CF78E]' :
                        creator.status === 'invited' ? 'bg-[#FFC857]/10 text-[#FFC857]' :
                        'bg-[#9CB0C7]/10 text-[#9CB0C7]'
                      }`}>
                        {creator.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
